import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import type {
  PickerRowsPage,
  UidGroupsOfProductsArgs,
} from '../canonicalPicker.types'
import { allRowsOf, BaseQuery } from './paging'
import {
  chunksOf,
  groupsOfProductsUrl,
  isScopedSource,
  PRODUCTS_PER_REQUEST,
  rowsByKeyUrls,
} from './query'
import {
  groupReaching,
  isUidGroupRow,
  pageOf,
  productsByVariantGroupOf,
} from './rows'

type Result = { data: PickerRowsPage } | { error: FetchBaseQueryError }

/**
 * The UID groups behind smart picks: those filed under the products, then
 * those the products' own variants name that aren't, since dbt can file a
 * merged product's group under its other id. Each reaches the products that
 * name it, so the picks it stands for keep their level 2.
 */
const uidGroupsOfProducts = async (
  { source, variantsSource, productIds, scope }: UidGroupsOfProductsArgs,
  baseQuery: BaseQuery
): Promise<Result> => {
  const filed = await allRowsOf(
    chunksOf(productIds, PRODUCTS_PER_REQUEST).map(
      chunk => page => groupsOfProductsUrl(source, chunk, page)
    ),
    baseQuery
  )
  if ('error' in filed) return filed
  const filedPage = pageOf(filed.data, source, 'uidGroup', false)
  if (!variantsSource) return { data: filedPage }

  const variants = await allRowsOf(
    rowsByKeyUrls({
      source: variantsSource,
      kind: 'product',
      keys: productIds,
      scope: isScopedSource(variantsSource) ? scope : null,
    }),
    baseQuery
  )
  if ('error' in variants) return variants
  const named = productsByVariantGroupOf(
    pageOf(variants.data, variantsSource, 'product', true).rows
  )

  const filedIds = new Set(filedPage.rows.map(row => row.id))
  const unfiled = new Set(
    Array.from(named.keys()).filter(id => !filedIds.has(id))
  )
  const found = await allRowsOf(
    rowsByKeyUrls({
      source,
      kind: 'uidGroup',
      // A group row id is provider:uid_group, and the source is keyed by uid_group
      keys: Array.from(
        new Set(Array.from(unfiled, id => id.slice(id.indexOf(':') + 1)))
      ),
      scope: null,
    }),
    baseQuery
  )
  if ('error' in found) return found
  const extra = pageOf(found.data, source, 'uidGroup', false).rows.filter(row =>
    unfiled.has(row.id)
  )

  const rows = [...filedPage.rows, ...extra]
    .filter(isUidGroupRow)
    .map(group => groupReaching(group, named.get(group.id) ?? []))
  return {
    data: { rows, totalCount: filedPage.totalCount + extra.length },
  }
}

export default uidGroupsOfProducts
